import { useState } from 'react'
import { Plus, Edit2, Trash2, Eye, EyeOff, Heart, Users, AlertCircle, CheckCircle, Loader2 } from 'lucide-react'
import { useCollection, useFileUpload } from '../../hooks/useFirestore'
import { ministries as defaultMinistries } from '../../data/seedData'
import { Button, Modal, Input, Textarea, Select, Card, Badge, Checkbox } from '../../components/common'

const categories = [
  { value: 'men', label: 'Men' },
  { value: 'women', label: 'Women' },
  { value: 'youth', label: 'Youth' },
  { value: 'children', label: 'Children' },
  { value: 'groups', label: 'Small Groups' },
  { value: 'worship', label: 'Worship' },
  { value: 'outreach', label: 'Outreach' },
  { value: 'prayer', label: 'Prayer' },
]

const MinistryForm = ({ ministry, onSave, onClose }) => {
  const [formData, setFormData] = useState(ministry || {
    name: '',
    description: '',
    category: '',
    leaderName: '',
    schedule: '',
    location: '',
    imageUrl: '',
    isActive: true,
  })
  const [saving, setSaving] = useState(false)
  const { upload, uploading } = useFileUpload()

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    const result = await upload(file, `ministries/${Date.now()}_${file.name}`)
    if (result.success) {
      setFormData({ ...formData, imageUrl: result.url })
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    await onSave(formData)
    setSaving(false)
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Input
        label="Ministry Name"
        required
        value={formData.name}
        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        placeholder="Men's Fellowship"
      />

      <Textarea
        label="Description"
        rows={3}
        required
        value={formData.description}
        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
        placeholder="Describe the ministry..."
      />

      <div className="grid md:grid-cols-2 gap-4">
        <Select
          label="Category"
          required
          options={categories}
          value={formData.category}
          onChange={(e) => setFormData({ ...formData, category: e.target.value })}
        />
        <Input
          label="Ministry Leader"
          value={formData.leaderName}
          onChange={(e) => setFormData({ ...formData, leaderName: e.target.value })}
          placeholder="Brother John Smith"
        />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Input
          label="Schedule"
          value={formData.schedule}
          onChange={(e) => setFormData({ ...formData, schedule: e.target.value })}
          placeholder="Every Saturday at 7:00 AM"
        />
        <Input
          label="Location"
          value={formData.location}
          onChange={(e) => setFormData({ ...formData, location: e.target.value })}
          placeholder="Fellowship Hall"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Ministry Image
        </label>
        <div className="flex items-center gap-4">
          {formData.imageUrl && (
            <img
              src={formData.imageUrl}
              alt="Preview"
              className="w-24 h-16 object-cover rounded-lg"
            />
          )}
          <label className="px-4 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
            <span className="text-sm">{uploading ? 'Uploading...' : 'Upload Image'}</span>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
            />
          </label>
        </div>
      </div>

      <Checkbox
        label="Active"
        checked={formData.isActive}
        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
      />

      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button type="button" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" loading={saving}>
          {ministry ? 'Update Ministry' : 'Create Ministry'}
        </Button>
      </div>
    </form>
  )
}

const MinistriesManager = () => {
  const [showModal, setShowModal] = useState(false)
  const [editingMinistry, setEditingMinistry] = useState(null)
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  const [actionError, setActionError] = useState(null)
  const [actionSuccess, setActionSuccess] = useState(null)

  const { data: ministries, loading, error, add, update, remove } = useCollection('ministries', {
    orderByField: 'createdAt',
    orderDirection: 'desc',
  })

  // Sort by name for display
  const sortedMinistries = ministries?.length > 0
    ? [...ministries].sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    : []

  const displayMinistries = sortedMinistries.length > 0 ? sortedMinistries : defaultMinistries

  const showMessage = (message, isError = false) => {
    if (isError) {
      setActionError(message)
      setTimeout(() => setActionError(null), 5000)
    } else {
      setActionSuccess(message)
      setTimeout(() => setActionSuccess(null), 3000)
    }
  }

  const handleSave = async (data) => {
    try {
      // Check if this ministry exists in Firestore (not just seed data)
      const isRealMinistry = ministries?.some(m => m.id === editingMinistry?.id)

      if (editingMinistry?.id && isRealMinistry) {
        // Update existing Firestore document
        const result = await update(editingMinistry.id, data)
        if (result.success === false) throw new Error(result.error?.message || 'Failed to update')
        showMessage('Ministry updated successfully!')
      } else {
        // Create new document (either new ministry or seed data being edited)
        const result = await add(data)
        if (result.success === false) throw new Error(result.error?.message || 'Failed to create')
        showMessage(editingMinistry ? 'Ministry saved to database!' : 'Ministry created successfully!')
      }
      setShowModal(false)
      setEditingMinistry(null)
    } catch (err) {
      console.error('Save error:', err)
      showMessage(err.message || 'Failed to save ministry. Please try again.', true)
    }
  }

  const handleDelete = async () => {
    if (deleteConfirm) {
      // Check if this is a real Firestore ministry
      const isRealMinistry = ministries?.some(m => m.id === deleteConfirm)

      if (!isRealMinistry) {
        showMessage('This is sample data. Edit it first to save to the database, then you can delete it.', true)
        setDeleteConfirm(null)
        return
      }

      try {
        const result = await remove(deleteConfirm)
        if (result.success === false) throw new Error(result.error?.message || 'Failed to delete')
        showMessage('Ministry deleted successfully!')
        setDeleteConfirm(null)
      } catch (err) {
        console.error('Delete error:', err)
        showMessage(err.message || 'Failed to delete ministry. Please try again.', true)
        setDeleteConfirm(null)
      }
    }
  }

  const toggleActive = async (ministry) => {
    // Check if this is a real Firestore ministry
    const isRealMinistry = ministries?.some(m => m.id === ministry.id)

    if (!isRealMinistry) {
      showMessage('This is sample data. Edit and save it first to enable this feature.', true)
      return
    }

    try {
      const result = await update(ministry.id, { isActive: !ministry.isActive })
      if (result.success === false) throw new Error(result.error?.message || 'Failed to update')
      showMessage(`Ministry ${ministry.isActive ? 'hidden' : 'published'}!`)
    } catch (err) {
      console.error('Toggle error:', err)
      showMessage(err.message || 'Failed to update ministry status.', true)
    }
  }

  // Check if a ministry is from Firestore or seed data
  const isRealMinistry = (ministry) => ministries?.some(m => m.id === ministry.id)

  return (
    <div className="space-y-6">
      {/* Error/Success Messages */}
      {actionError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          {actionError}
        </div>
      )}
      {actionSuccess && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          {actionSuccess}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-dark-100">Ministries</h1>
          <p className="text-gray-500">Manage church ministries and groups</p>
          {error && <p className="text-red-500 text-sm mt-1">Error loading data: {error.message}</p>}
        </div>
        <Button
          variant="primary"
          icon={Plus}
          onClick={() => {
            setEditingMinistry(null)
            setShowModal(true)
          }}
        >
          Add Ministry
        </Button>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        </div>
      )}

      {/* Ministries Grid */}
      {!loading && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {displayMinistries.map((ministry) => (
          <Card key={ministry.id} animate={false} padding={false} className="overflow-hidden">
            <div className="relative h-40">
              {ministry.imageUrl ? (
                <img
                  src={ministry.imageUrl}
                  alt={ministry.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-primary-100 to-secondary-100 flex items-center justify-center">
                  <Heart className="w-12 h-12 text-primary-300" />
                </div>
              )}
              {!ministry.isActive && (
                <div className="absolute top-2 left-2">
                  <Badge variant="gray">Inactive</Badge>
                </div>
              )}
              {!isRealMinistry(ministry) && (
                <div className="absolute top-2 right-2">
                  <Badge variant="warning">Sample Data</Badge>
                </div>
              )}
            </div>

            <div className="p-4">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h3 className="font-semibold text-dark-100">{ministry.name}</h3>
                  <Badge variant="secondary" size="sm">{ministry.category}</Badge>
                </div>
              </div>

              <p className="text-sm text-gray-600 line-clamp-2 mb-3">{ministry.description}</p>

              {ministry.leaderName && (
                <div className="flex items-center gap-1 text-sm text-gray-500 mb-1">
                  <Users className="w-4 h-4" />
                  {ministry.leaderName}
                </div>
              )}

              <div className="flex items-center justify-end gap-1 mt-3 pt-3 border-t">
                <button
                  onClick={() => toggleActive(ministry)}
                  className={`p-2 rounded-lg transition-colors ${
                    ministry.isActive
                      ? 'text-emerald-600 hover:bg-emerald-50'
                      : 'text-gray-400 hover:bg-gray-100'
                  }`}
                >
                  {ministry.isActive ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                </button>
                <button
                  onClick={() => {
                    setEditingMinistry(ministry)
                    setShowModal(true)
                  }}
                  className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg"
                >
                  <Edit2 className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setDeleteConfirm(ministry.id)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            </div>
          </Card>
        ))}
        </div>
      )}

      {!loading && displayMinistries.length === 0 && (
        <div className="text-center py-12 text-gray-500 bg-white rounded-xl">
          <Heart className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>No ministries yet</p>
          <Button
            variant="ghost"
            size="sm"
            className="mt-2"
            onClick={() => setShowModal(true)}
          >
            Add Your First Ministry
          </Button>
        </div>
      )}

      {/* Create/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false)
          setEditingMinistry(null)
        }}
        title={editingMinistry ? 'Edit Ministry' : 'Add Ministry'}
        size="lg"
      >
        <MinistryForm
          ministry={editingMinistry}
          onSave={handleSave}
          onClose={() => {
            setShowModal(false)
            setEditingMinistry(null)
          }}
        />
      </Modal>

      {/* Delete Confirmation */}
      <Modal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Delete Ministry"
        size="sm"
      >
        <p className="text-gray-600 mb-6">
          Are you sure you want to delete this ministry? This action cannot be undone.
        </p>
        <div className="flex justify-end gap-3">
          <Button variant="ghost" onClick={() => setDeleteConfirm(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </div>
      </Modal>
    </div>
  )
}

export default MinistriesManager
